import { DateTime } from "luxon";

import { DateUnit, PERIOD_FORMATS, periodsBetween } from "./spendChartRows";

/**
 * One period of the buyer-supplier chart, plus the period start its click handler filters by and
 * the counts the period's modal shows on its tabs.
 */
export type BuyerSupplierRow = {
  period: string;
  periodStart: string;
  spend: number;
  awardValue: number;
  transactions: number;
  awardCount: number;
};

type Args = {
  spendPoints: { period: string; totalValue: number; transactionCount?: number }[];
  awardPoints?: { period: string; awardValue?: number | null; awardCount?: number }[];
  from: DateTime;
  to: DateTime;
  dateUnit: DateUnit;
};

/**
 * Buckets spend and award value into one row per period from `from` to `to`, so a quiet period
 * shows as a gap rather than collapsing. Anything dated outside the range is dropped.
 */
export function toBuyerSupplierRows({
  spendPoints,
  awardPoints = [],
  from,
  to,
  dateUnit,
}: Args): BuyerSupplierRow[] {
  const rows = new Map<string, BuyerSupplierRow>(
    periodsBetween(from.startOf(dateUnit), to.startOf(dateUnit), dateUnit).map((date) => {
      const periodStart = date.toISODate() as string;
      return [
        periodStart,
        {
          period: date.toFormat(PERIOD_FORMATS[dateUnit]),
          periodStart,
          spend: 0,
          awardValue: 0,
          transactions: 0,
          awardCount: 0,
        },
      ];
    }),
  );

  const rowFor = (isoDate: string) => {
    const date = DateTime.fromISO(isoDate);
    return date.isValid ? rows.get(date.startOf(dateUnit).toISODate() as string) : undefined;
  };

  for (const point of spendPoints) {
    const row = rowFor(point.period);
    if (row) {
      row.spend += point.totalValue;
      row.transactions += point.transactionCount ?? 0;
    }
  }

  for (const point of awardPoints) {
    const row = rowFor(point.period);
    if (row) {
      row.awardValue += point.awardValue ?? 0;
      row.awardCount += point.awardCount ?? 0;
    }
  }

  return [...rows.values()];
}
